<template>      
   <section class="px-4 mx-auto max-w-5xl xl:px-0">
    <div class="border-t border-gray-200" :style="{ backgroundColor: sectionBackgroundColor }">
            <div class="flex flex-col md:flex-row">
                <!-- Left Image -->
                <nuxt-img class="object-cover object-top w-full lg:w-96 sm:w-72 md-max:mx-auto md-max:max-h-96" :src="leftImageSrc" sizes="xs:283px sm:288px md:288px lg:288px xl:384px" :alt="leftImageAlt" width="384" height="712" loading="lazy" />
                
                <!-- Right Content -->
                <div class="p-5 w-full sm:p-8 lg:p-16">
                    <h2 class="text-2xl leading-none text-center lg:text-32 md:text-2xl font-ww-bold md:text-left" v-html="mainHeading">
                    </h2>
                    
                    <div class="mt-4 mb-4 text-sm leading-6 font-ww-bold lg:text-lg sm:text-base" v-html="subHeading">
                    </div>
                    
                    <div class="space-y-4 lg:text-lg sm:text-base text-sm !leading-7">
                        <div v-for="(paragraph, index) in contentParagraphs" :key="index" v-html="paragraph.text">
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="pb-10 mx-auto mt-10 lg:mt-20 md:max-w-4xl xl:px-10">
            <h3 class="text-2xl leading-none text-center lg:text-3xl md:text-2xl font-ww-bold xl:px-12" v-html="bottomHeading">
                    </h3>

                    <div class="mt-4 text-base lg:mt-8 xl:px-6 md:text-17" v-html="bottomText">
                    </div>
        </div>
   </section>
</template>

<script setup>
defineProps({
  sectionBackgroundColor: {
    type: String,
    default: '#F8FAFC'
  },
  leftImageSrc: {
    type: String,
    default: '/assets/images/athletic-man-drinking-something-after-training.jpg'
  },
  leftImageAlt: {
    type: String,
    default: 'Man drinking energy drink'
  },
  mainHeading: {
    type: String,
    default: 'The Real Cause of Jitters, Crashes, and Brain Fog: "Dirty Caffeine"'
  },
  subHeading: {
    type: String,
    default: 'Energy drinks, jitter bombs, anxiety-triggering "lattes" (sugar factories) – modern caffeine is chaos.'
  },
  contentParagraphs: {
    type: Array,
    default: () => [
      {
        text: 'The real problem is NOT you or your body, it\'s the garbage others are pushing on you.'
      },
      {
        text: '<span class="font-semibold">First</span>, you need to avoid cheap, low-quality coffee beans which are sprayed with gut-harming pesticides such as the cancer-causing glyphosate.'
      },
      {
        text: '(No wonder you\'re crashing!)'
      },
      {
        text: 'All the makers and pushers of low-quality coffees and energy drinks just want to jack you up with more and more caffeine because what they offer doesn\'t really work–those drinks don\'t provide you instant, all-day energy without crashes.'
      },
      {
        text: '<span class="font-semibold">Second</span>, you need to enhance your caffeine, not consume more of it, if you want the greatest energy and focus.'
      }
    ]
  },
  bottomHeading: {
    type: String,
    default: 'Either "Engineer" Your Morning–Or Suffer from Low Energy, Brain Fog, and Lost Productivity'
  },
  bottomText: {
    type: String,
    default: 'Either you keep dousing yourself with more and more caffeine, risking adrenal gland burnout ...while suffering through brain fog, crashes, jitters, and low energy…missing HALF of your day\'s productivity…<br><br>Or, start optimizing how your body uses caffeine for good, enjoying the sustained energy you were meant to have.'
  }
})

defineBuilderComponent({
    friendlyName: 'Image Left Text Right',
    canHaveChildren: false,
    inputs: [
        {
            name: 'sectionBackgroundColor',
            type: 'color',
            defaultValue: '#F8FAFC'
        },
        {
            name: 'leftImageSrc',
            type: 'file',
            allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
            defaultValue: '/assets/images/athletic-man-drinking-something-after-training.jpg'
        },
        {
            name: 'leftImageAlt',
            type: 'text',
            defaultValue: 'Man drinking energy drink'
        },
        {
            name: 'mainHeading',
            type: 'richText',
            defaultValue: 'The Real Cause of Jitters, Crashes, and Brain Fog: "Dirty Caffeine"'
        },
        {
            name: 'subHeading',
            type: 'richText',
            defaultValue: 'Energy drinks, jitter bombs, anxiety-triggering "lattes" (sugar factories) – modern caffeine is chaos.'
        },
        {
            name: 'contentParagraphs',
            type: 'list',
            subFields: [
                {
                    name: 'text',
                    type: 'richText',
                    defaultValue: ''
                }
            ],
            defaultValue: [
                {
                    text: 'The real problem is NOT you or your body, it\'s the garbage others are pushing on you.'
                },
                {
                    text: '<span class="font-semibold">First</span>, you need to avoid cheap, low-quality coffee beans which are sprayed with gut-harming pesticides such as the cancer-causing glyphosate.'
                },
                {
                    text: '(No wonder you\'re crashing!)'
                },
                {
                    text: 'All the makers and pushers of low-quality coffees and energy drinks just want to jack you up with more and more caffeine because what they offer doesn\'t really work–those drinks don\'t provide you instant, all-day energy without crashes.'
                },
                {
                    text: '<span class="font-semibold">Second</span>, you need to enhance your caffeine, not consume more of it, if you want the greatest energy and focus.'
                }
            ]
        },
        {
            name: 'bottomHeading',
            type: 'richText',
            defaultValue: 'Either "Engineer" Your Morning–Or Suffer from Low Energy, Brain Fog, and Lost Productivity'
        },
        {
            name: 'bottomText',
            type: 'richText',
            defaultValue: 'Either you keep dousing yourself with more and more caffeine, risking adrenal gland burnout ...while suffering through brain fog, crashes, jitters, and low energy…missing HALF of your day\'s productivity…<br><br>Or, start optimizing how your body uses caffeine for good, enjoying the sustained energy you were meant to have.'
        }
    ]
})
</script>

<style scoped>
</style>